import * as THREE from "three";

/**
 * Follow-mode x-ray (ADR 103): while the camera follows an asset, warehouse
 * fragments inside the camera→target capsule (or within a bubble around the
 * target) dither away so walls never block the view. Screen-space dither
 * (interleaved gradient noise) instead of alpha: no transparency sorting, no
 * material surgery — safe on shared/cached GLTF materials.
 *
 * Every patched material references THESE uniform objects, so the per-frame
 * update in CameraDirector reaches all of them for free.
 */
export const xrayUniforms = {
  uXrayOn:     { value: 0 },
  uXrayA:      { value: new THREE.Vector3() }, // camera position
  uXrayB:      { value: new THREE.Vector3() }, // followed asset position
  uXrayRadius: { value: 3.0 },                 // clear bubble around the target (m)
};

const FRAG_HEADER = `
varying vec3 vXrayWorld;
uniform float uXrayOn;
uniform vec3 uXrayA;
uniform vec3 uXrayB;
uniform float uXrayRadius;
float xrayDistToSegment(vec3 p, vec3 a, vec3 b) {
  vec3 ab = b - a;
  float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 1e-6), 0.0, 1.0);
  return length(p - (a + ab * t));
}
`;

const FRAG_BODY = `
if (uXrayOn > 0.5) {
  float dSeg = xrayDistToSegment(vXrayWorld, uXrayA, uXrayB);
  float dTgt = distance(vXrayWorld, uXrayB);
  float r = min(dSeg / 1.2, dTgt / uXrayRadius);
  if (r < 1.0) {
    float keep = mix(0.18, 1.0, smoothstep(0.72, 1.0, r));
    float ign = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
    if (ign > keep) discard;
  }
}
`;

/** Idempotent; inert while uXrayOn = 0 (shared drei-cache materials are fine). */
export function patchMaterialForXray(mat: THREE.Material): void {
  // Test renderers hand back material stubs with no userData, and a layer must
  // not fail to mount because it cannot be x-rayed. Nothing to patch, no-op.
  if (!mat || !mat.userData) return;
  if (mat.userData.xrayPatched) return;
  mat.userData.xrayPatched = true;
  // Scene-graph materials are patched from a ref callback, i.e. after three may
  // already have compiled the program. Without this the shader is only swapped
  // in on some other invalidation, so the first follow of a session x-rays the
  // GLB and nothing else.
  mat.needsUpdate = true;
  mat.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, xrayUniforms);
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", "#include <common>\nvarying vec3 vXrayWorld;")
      .replace(
        "#include <worldpos_vertex>",
        `#include <worldpos_vertex>
        vec4 xrayLocal = vec4(transformed, 1.0);
        #ifdef USE_INSTANCING
          xrayLocal = instanceMatrix * xrayLocal;
        #endif
        vXrayWorld = (modelMatrix * xrayLocal).xyz;`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", "#include <common>\n" + FRAG_HEADER)
      .replace("#include <dithering_fragment>", "#include <dithering_fragment>\n" + FRAG_BODY);
  };
}

/**
 * Ref callback for declarative materials: `<meshStandardMaterial ref={xrayRef} />`.
 *
 * The x-ray shipped patched onto the warehouse GLB only (ADR 103). Racks, decks
 * and pallets are separate layers with their own materials, so following an
 * asset down an aisle dissolved the walls and left the racking — the geometry
 * actually between the camera and the truck — fully opaque. ADR 340.
 */
export const xrayRef = (m: THREE.Material | null): void => {
  if (m) patchMaterialForXray(m);
};
